import { toast } from "@stores/toastStore";
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";

function statusOf(error: unknown): number | null {
  if (error instanceof Error && "status" in error) {
    const status = (error as Error & { status: number }).status;
    return typeof status === "number" ? status : null;
  }
  return null;
}

export const queryClient = new QueryClient({
  // Centralised error surfacing. Per-call `onError` handlers still
  // win — if a query/mutation sets `meta.silent: true` we stay quiet,
  // letting the caller render its own banner. 401s are handled by the
  // axios interceptor (redirect to /login), so we never toast them.
  //
  // 404s on queries are also suppressed: a missing-resource read is a
  // life-cycle event (just-deleted, never-existed, scope changed) that
  // the consumer is expected to render as an empty state. Surfacing
  // those globally double-toasts the post-delete success — the user
  // sees `Deleted 'X'` immediately followed by `Project not found`
  // from the briefly-still-mounted detail/stats query racing the
  // unmount. Mutations still toast 404 (they're explicit user actions
  // and a 404 there is genuinely surprising).
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.meta?.silent) return;
      const status = statusOf(error);
      if (status === 401 || status === 403 || status === 404) return;
      const message =
        error instanceof Error
          ? error.message
          : "Something went wrong while loading data.";
      toast.error(message);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      // Mutations that opted in via `meta.silent` or that wired their
      // own `onError` get to handle the surfacing themselves. The
      // default mutation `onError` callback is per-call and runs in
      // addition to this — if it ran, the mutation set `meta.handled`
      // so we don't double-toast.
      if (mutation.meta?.silent) return;
      if (mutation.options.onError) return;
      const status = statusOf(error);
      if (status === 401 || status === 403) return;
      const message =
        error instanceof Error
          ? error.message
          : "Something went wrong. Please try again.";
      toast.error(message);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2, // 2 minutes
      retry: (failureCount, error) => {
        // Don't retry on 401/403
        if (error instanceof Error && "status" in error) {
          const status = (error as Error & { status: number }).status;
          if (status === 401 || status === 403) return false;
        }
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});
